import type {AbstractSketchEdgeObject} from '../mesh/sketch/AbstractSketchEdgeObject';
import {isAbstractSketchEdgeObject} from '../mesh/guards/isAbstractSketchEdgeObject';
import {isSketchLineObject} from '../mesh/guards/isSketchLineObject';
import type {SketchRelationDefinition} from '../sketch/solver/constraint/relationDefinition/SketchRelationDefinition';
import {MultiTargetRelation} from '../sketch/solver/constraint/relationDefinition/MultiTargetRelation';
import {SingleTargetRelation} from '../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import {SketchRelationKind} from '../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import * as THREE from 'three';
import {SketchRelationMarkerLabelObject} from '../mesh/SketchRelationMarkerLabelObject';
import {
    type MarkerDefinition,
    type MarkerStateMachineInput,
    RelationMarkerVisualState,
    type RelationMarkerRecord,
} from './relation/SketchRelationMarkerTypes';
import {
    MARKER_VISUAL_STYLE_MAP,
    SVG_BG_COLOR_TOKEN,
    SVG_GLYPH_COLOR_TOKEN,
    SVG_URL_MAP,
} from './relation/SketchRelationMarkerConstants';
import { addObject3DToParent } from '../utils/scene-object/addObject3DToParent';
import type {Viewer} from '../viewer/Viewer';


/**
 * 关系标签布局。
 * 说明：
 * 1. `anchorPosition` 是标签挂载到草图对象下时使用的局部 3D 锚点；
 * 2. `screenOffset` 是最终显示时相对锚点的 CSS 像素偏移；
 * 3. 将两者拆开可以避免世界单位偏移在透视投影下被放大。
 */
interface MarkerLayout {
    /** 标签 3D 锚点位置。 */
    anchorPosition: THREE.Vector3;
    /** 标签屏幕像素偏移。 */
    screenOffset: THREE.Vector2;
}


/**
 * 几何关系标签状态类。
 * 职责：
 * 1. 传入 THREE.Object3D 后识别 `AbstractSketchEdgeObject`；
 * 2. 读取 `constraints` 并按约束生成标签；
 * 3. 多目标约束在所有关联目标对象上同步创建对应标签。
 */
export class SketchGeometryRelationLabelManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 图标尺寸（像素）。 */
    private static readonly MARKER_ICON_SIZE_PX = 14;
    
    /** 图标相对几何中点的屏幕偏移距离（像素）。 */
    private static readonly MARKER_SCREEN_OFFSET_PX = 18;
    /** 多个图标沿切线方向展开的屏幕距离（像素）。 */
    private static readonly MARKER_TANGENT_SCREEN_SPACING_PX = 18;
    /** 多层图标沿法线方向展开的屏幕距离（像素）。 */
    private static readonly MARKER_LAYER_SCREEN_SPACING_PX = 18;
    
    /** 标签记录映射（按对象 ID）。 */
    private readonly markerRecordsByObjectId = new Map<string, RelationMarkerRecord>();
    /** SVG 模板缓存。 */
    private readonly svgTemplatePromiseByKind = new Map<SketchRelationKind, Promise<string>>();
    /** 菜单选中标签键集合。 */
    private readonly menuSelectedMarkerKeySet = new Set<string>();
    /** 点击选中标签键集合。 */
    private readonly clickSelectedMarkerKeySet = new Set<string>();
    /** 鼠标移入标签键集合。 */
    private readonly hoveredMarkerKeySet = new Set<string>();
    /** 是否强制隐藏关系标签。 */
    private markerForceHidden = false;

    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 同步目标对象内的几何关系标签。
     * @param object 目标对象。
     * @param visible 标签可见性。
     * @returns 实际创建/更新标签的对象 ID 列表。
     */
    public syncObjectMarkers(object: THREE.Object3D, visible: boolean): string[] {
        const markerDefinitionMap = this.collectMarkerDefinitions(object);

        markerDefinitionMap.forEach((definitions, objectId) => {
            this.ensureObjectMarkers(objectId, definitions, visible);
        });

        return Array.from(markerDefinitionMap.keys());
    }

    /**
     * 刷新草图对象上所有关系标签的显示状态。
     * 自动检测关系的添加和删除：新增关系创建对应标签，已删除关系移除对应标签。
     * @param object 草图对象（可以是单个边对象或包含多个边对象的父节点）。
     * @param visible 标签可见性。
     * @returns 实际关联标签的对象 ID 列表。
     */
    public refreshObjectMarkers(object: THREE.Object3D, visible: boolean): string[] {
        // 收集当前对象树下已跟踪的全部对象 ID。
        const previousTrackedIds = this.collectTrackedObjectIds(object);
        // 重新收集标签定义。
        const nextDefinitionMap = this.collectMarkerDefinitions(object);

        // 清理已删除关系对应的标签（旧记录中存在但新定义中不存在的对象）。
        previousTrackedIds.forEach((objectId) => {
            if (!nextDefinitionMap.has(objectId)) {
                this.clearObjectMarkers(objectId);
            }
        });

        // 同步新的标签定义（含新增和更新）。
        nextDefinitionMap.forEach((definitions, objectId) => {
            this.ensureObjectMarkers(objectId, definitions, visible);
        });

        return Array.from(nextDefinitionMap.keys());
    }

    /**
     * 移除草图对象上指定约束的关系标签 CSS 对象。
     * @param object 草图对象（可以是单个边对象或包含多个边对象的父节点）。
     * @param constraintId 约束 ID。
     */
    public removeConstraintMarker(object: THREE.Object3D, constraintId: number): void {
        // 遍历对象树，在所有匹配的记录中移除目标约束标签。
        object.traverse((child) => {
            this.removeConstraintMarkerFromRecord(child.uuid, constraintId);
        });
    }

    /**
     * 清理指定对象的关系标签。
     * @param objectId 对象 ID。
     */
    public clearObjectMarkers(objectId: string): void {
        const record = this.markerRecordsByObjectId.get(objectId);
        if (!record) {
            return;
        }

        record.markerObjects.forEach((markerObject) => {
            this.detachMarkerObject(markerObject);
        });
        this.markerRecordsByObjectId.delete(objectId);
    }

    /**
     * 获取指定对象的标签键列表。
     * @param objectId 对象 ID。
     * @returns 标签键列表。
     */
    public getObjectMarkerKeys(objectId: string): string[] {
        const record = this.markerRecordsByObjectId.get(objectId);
        if (!record) {
            return [];
        }
        return [...record.markerKeys];
    }

    /**
     * 清理全部标签。
     */
    public clearAllMarkers(): void {
        Array.from(this.markerRecordsByObjectId.keys()).forEach((objectId) => {
            this.clearObjectMarkers(objectId);
        });
    }

    /**
     * 释放状态资源。
     */
    public dispose(): void {
        this.clearAllMarkers();
        this.clearMarkerVisualStates();
    }

    /**
     * 同步状态机输入。
     * @param input 状态机输入参数。
     */
    public syncMarkerStateMachine(input: MarkerStateMachineInput): void {
        this.replaceStateKeySet(this.menuSelectedMarkerKeySet, input.menuSelectedMarkerKeys ?? []);
        this.replaceStateKeySet(this.clickSelectedMarkerKeySet, input.clickSelectedMarkerKeys ?? []);
        this.replaceStateKeySet(this.hoveredMarkerKeySet, input.hoveredMarkerKeys ?? []);
        this.refreshAllMarkerVisualStates();
    }

    /**
     * 清理状态机输入状态。
     */
    public clearMarkerVisualStates(): void {
        this.menuSelectedMarkerKeySet.clear();
        this.clickSelectedMarkerKeySet.clear();
        this.hoveredMarkerKeySet.clear();
        this.refreshAllMarkerVisualStates();
    }

    /**
     * 设置关系标签强制隐藏状态。
     * @param forceHidden 是否强制隐藏。
     */
    public setMarkerForceHidden(forceHidden: boolean): void {
        if (this.markerForceHidden === forceHidden) {
            return;
        }
        this.markerForceHidden = forceHidden;
        this.refreshAllMarkerVisualStates();
    }

    /**
     * 收集标签定义。
     * @param object 目标对象。
     * @returns 对象 ID 到标签定义列表的映射。
     */
    private collectMarkerDefinitions(object: THREE.Object3D): Map<string, MarkerDefinition[]> {
        const markerDefinitionMap = new Map<string, MarkerDefinition[]>();
        const dedupeKeySet = new Set<string>();

        this.collectEdgeObjects(object).forEach((edgeObject) => {
            edgeObject.constraints.getRelations().forEach((constraint) => {
                const relationKind = this.parseRelationKind(constraint.relationType);
                if (!relationKind) {
                    return;
                }

                if (constraint instanceof MultiTargetRelation) {
                    const targetObjects = this.resolveMultiTargetConstraintOwners(constraint, edgeObject);
                    targetObjects.forEach((targetObject) => {
                        this.pushMarkerDefinition(
                            markerDefinitionMap,
                            dedupeKeySet,
                            targetObject,
                            relationKind,
                            constraint.id,
                        );
                    });
                    return;
                }

                if (constraint instanceof SingleTargetRelation) {
                    this.pushMarkerDefinition(
                        markerDefinitionMap,
                        dedupeKeySet,
                        edgeObject,
                        relationKind,
                        constraint.id,
                    );
                    return;
                }

                // 未知约束类型按单目标处理，避免丢标签。
                this.pushMarkerDefinition(
                    markerDefinitionMap,
                    dedupeKeySet,
                    edgeObject,
                    relationKind,
                    constraint.id,
                );
            });
        });

        return markerDefinitionMap;
    }

    /**
     * 收集边对象。
     * @param object 目标对象。
     * @returns 边对象列表。
     */
    private collectEdgeObjects(object: THREE.Object3D): AbstractSketchEdgeObject[] {
        const edgeMap = new Map<string, AbstractSketchEdgeObject>();

        if (isAbstractSketchEdgeObject(object)) {
            edgeMap.set(object.uuid, object);
            return Array.from(edgeMap.values());
        }

        object.traverse((child) => {
            if (!isAbstractSketchEdgeObject(child)) {
                return;
            }
            edgeMap.set(child.uuid, child);
        });
        return Array.from(edgeMap.values());
    }

    /**
     * 解析多目标约束对应的所有目标边对象。
     * @param constraint 多目标约束。
     * @param fallbackObject 回退对象。
     * @returns 目标边对象列表。
     */
    private resolveMultiTargetConstraintOwners(
        constraint: SketchRelationDefinition,
        fallbackObject: AbstractSketchEdgeObject,
    ): AbstractSketchEdgeObject[] {
        const owners = new Map<string, AbstractSketchEdgeObject>();
        const targetObjects = this.readConstraintTargets(constraint);
        targetObjects.forEach((targetObject) => {
            if (!isAbstractSketchEdgeObject(targetObject)) {
                return;
            }
            owners.set(targetObject.uuid, targetObject);
        });

        if (owners.size <= 0) {
            owners.set(fallbackObject.uuid, fallbackObject);
        }

        return Array.from(owners.values());
    }

    /**
     * 读取约束目标集合。
     * @param constraint 约束对象。
     * @returns 目标对象列表。
     */
    private readConstraintTargets(constraint: SketchRelationDefinition): THREE.Object3D[] {
        const rawTargets = (constraint as unknown as { targets?: Set<unknown> | unknown[] }).targets;
        if (!rawTargets) {
            return [];
        }

        if (rawTargets instanceof Set) {
            return Array.from(rawTargets.values()).filter((item): item is THREE.Object3D => item instanceof THREE.Object3D);
        }

        if (Array.isArray(rawTargets)) {
            return rawTargets.filter((item): item is THREE.Object3D => item instanceof THREE.Object3D);
        }

        return [];
    }

    /**
     * 推入标签定义。
     * @param markerDefinitionMap 标签定义映射。
     * @param dedupeKeySet 去重键集合。
     * @param owner 目标对象。
     * @param relationKind 关系类型。
     * @param constraintId 约束 ID。
     */
    private pushMarkerDefinition(
        markerDefinitionMap: Map<string, MarkerDefinition[]>,
        dedupeKeySet: Set<string>,
        owner: AbstractSketchEdgeObject,
        relationKind: SketchRelationKind,
        constraintId: number,
    ): void {
        const dedupeKey = `${owner.uuid}:${constraintId}`;
        if (dedupeKeySet.has(dedupeKey)) {
            return;
        }
        dedupeKeySet.add(dedupeKey);

        const definitions = markerDefinitionMap.get(owner.uuid) ?? [];
        definitions.push({
            owner,
            relationKind,
            constraintId,
        });
        markerDefinitionMap.set(owner.uuid, definitions);
    }

    /**
     * 解析关系类型。
     * @param rawType 原始关系类型。
     * @returns 关系类型或 null。
     */
    private parseRelationKind(rawType: unknown): SketchRelationKind | null {
        const relationKind = rawType as SketchRelationKind;
        // SVG 映射是关系标签能力的唯一来源，新增图标后无需再维护第二份显示白名单。
        if (typeof SVG_URL_MAP[relationKind] !== 'string') {
            return null;
        }

        return relationKind;
    }

    /**
     * 确保对象标签存在并更新。
     * @param objectId 对象 ID。
     * @param markerDefinitions 标签定义列表。
     * @param visible 标签可见性。
     */
    private ensureObjectMarkers(objectId: string, markerDefinitions: MarkerDefinition[], visible: boolean): void {
        if (markerDefinitions.length <= 0) {
            this.clearObjectMarkers(objectId);
            return;
        }

        const owner = markerDefinitions[0].owner;
        const markerTypes = markerDefinitions.map((definition) => definition.relationKind);
        const markerKeys = markerDefinitions.map((definition) => this.buildMarkerKey(owner.uuid, definition.constraintId));

        const currentRecord = this.markerRecordsByObjectId.get(objectId);
        const requiresRebuild = (
            !currentRecord
            || currentRecord.markerObjects.length !== markerDefinitions.length
            || this.hasMarkerTypeChanged(currentRecord.markerTypes, markerTypes)
            || this.hasMarkerKeyChanged(currentRecord.markerKeys, markerKeys)
        );

        if (requiresRebuild) {
            this.clearObjectMarkers(objectId);
            const nextRecord = this.createObjectMarkerRecord(owner, markerTypes, markerKeys);
            if (!nextRecord) {
                return;
            }
            this.markerRecordsByObjectId.set(objectId, nextRecord);
        }

        const record = this.markerRecordsByObjectId.get(objectId);
        if (!record) {
            return;
        }

        const layouts = this.buildMarkerLayouts(owner, record.markerTypes);
        this.updateMarkerObjectLayouts(record.markerObjects, record.markerTypes, record.markerKeys, layouts, visible);
    }

    /**
     * 创建对象标签记录。
     * @param owner 所属对象。
     * @param markerTypes 标签类型列表。
     * @param markerKeys 标签键列表。
     * @returns 标签记录或 null。
     */
    private createObjectMarkerRecord(
        owner: AbstractSketchEdgeObject,
        markerTypes: SketchRelationKind[],
        markerKeys: string[],
    ): RelationMarkerRecord | null {
        const markerObjects: SketchRelationMarkerLabelObject[] = [];

        for (let index = 0; index < markerTypes.length; index += 1) {
            const markerObject = this.createMarkerObject(markerTypes[index], markerKeys[index]);
            if (!markerObject) {
                markerObjects.forEach((existing) => {
                    this.detachMarkerObject(existing);
                });
                return null;
            }
        addObject3DToParent(markerObject, owner);
            markerObjects.push(markerObject);
        }

        return {
            objectId: owner.uuid,
            markerObjects,
            markerTypes: [...markerTypes],
            markerKeys: [...markerKeys],
        };
    }

    /**
     * 构建标签布局列表。
     * @param edgeObject 边对象。
     * @param markerTypes 标签类型列表。
     * @returns 标签布局列表。
     */

    private buildMarkerLayouts(edgeObject: THREE.Object3D, markerTypes: SketchRelationKind[]): MarkerLayout[] {
        // 只做转发：先判断对象类型，再调用对应的位置计算函数。
        if (isSketchLineObject(edgeObject)) {
            return this.buildSketchLineMarkerLayouts(edgeObject, markerTypes);
        }
        return this.buildGenericMarkerLayouts(edgeObject, markerTypes);
    }


    /**
     * @description 直接从 SketchLineObject 端点计算中点与切线。
     * @param edgeObject 线对象。
     * @returns 中点与切线；不可用时返回 null。
     */
    /**
     * @description 计算草图直线对象标签位置。
     * @param edgeObject 草图直线对象。
     * @param markerTypes 标签类型列表。
     * @returns 标签布局列表。
     */
    private buildSketchLineMarkerLayouts(edgeObject: THREE.Object3D, markerTypes: SketchRelationKind[]): MarkerLayout[] {
        const lineMidpointAndTangent = this.resolveSketchLineMidpointAndTangent(edgeObject);
        if (!lineMidpointAndTangent) {
            return this.buildGenericMarkerLayouts(edgeObject, markerTypes);
        }
        return this.buildMarkerLayoutsByBasis(
            edgeObject,
            markerTypes,
            lineMidpointAndTangent.midpoint,
            lineMidpointAndTangent.tangent,
        );
    }

    /**
     * @description 计算非草图直线对象标签位置。
     * @param edgeObject 非草图直线对象。
     * @param markerTypes 标签类型列表。
     * @returns 标签布局列表。
     */
    private buildGenericMarkerLayouts(edgeObject: THREE.Object3D, markerTypes: SketchRelationKind[]): MarkerLayout[] {
        const markerCount = markerTypes.length;
        const layouts: MarkerLayout[] = [];
        if (!(edgeObject instanceof THREE.Line)) {
            for (let index = 0; index < markerCount; index += 1) {
                layouts.push(this.createMarkerLayout(new THREE.Vector3(0, 0, 0), new THREE.Vector2(0, 0)));
            }
            return layouts;
        }
        const geometry = edgeObject.geometry as THREE.BufferGeometry;
        const positionAttribute = geometry.getAttribute('position');
        if (!positionAttribute || positionAttribute.count <= 0) {
            for (let index = 0; index < markerCount; index += 1) {
                layouts.push(this.createMarkerLayout(new THREE.Vector3(0, 0, 0), new THREE.Vector2(0, 0)));
            }
            return layouts;
        }
        const maxIndex = positionAttribute.count - 1;
        const middleIndex = Math.max(0, Math.min(maxIndex, Math.floor(maxIndex * 0.5)));
        const midpoint = new THREE.Vector3(
            positionAttribute.getX(middleIndex),
            positionAttribute.getY(middleIndex),
            positionAttribute.getZ(middleIndex),
        );
        const tangent = this.computeTangent(positionAttribute, middleIndex);
        return this.buildMarkerLayoutsByBasis(edgeObject, markerTypes, midpoint, tangent);
    }

    /**
     * @description 基于中点和切线统一构建标签布局。
     * @param edgeObject 边对象。
     * @param markerTypes 标签类型列表。
     * @param midpoint 中点位置。
     * @param tangent 切线方向。
     * @returns 标签布局列表。
     */
    private buildMarkerLayoutsByBasis(
        edgeObject: THREE.Object3D,
        markerTypes: SketchRelationKind[],
        midpoint: THREE.Vector3,
        tangent: THREE.Vector3,
    ): MarkerLayout[] {
        const markerCount = markerTypes.length;
        const layouts: MarkerLayout[] = [];
        const placementCenter = this.resolveParentCenterInObjectSpace(edgeObject);

        for (let markerIndex = 0; markerIndex < markerCount; markerIndex += 1) {
            const markerType = markerTypes[markerIndex];
            const offsetDirection = this.resolveStateOffsetDirection(
                markerType,
                tangent,
                midpoint,
                placementCenter,
            );
            const normalOffsetLength = markerCount === 1
                ? SketchGeometryRelationLabelManager.MARKER_SCREEN_OFFSET_PX
                : SketchGeometryRelationLabelManager.MARKER_SCREEN_OFFSET_PX
                    + Math.floor(markerIndex / 2) * SketchGeometryRelationLabelManager.MARKER_LAYER_SCREEN_SPACING_PX;
            const normalScreenOffset = this.resolveScreenOffset(
                edgeObject,
                midpoint,
                offsetDirection,
                normalOffsetLength,
            );
            if (markerCount === 1) {
                layouts.push(this.createMarkerLayout(midpoint, normalScreenOffset));
                continue;
            }
            const sideSign = markerIndex % 2 === 0 ? -1 : 1;
            const tangentScreenOffset = this.resolveScreenOffset(
                edgeObject,
                midpoint,
                tangent,
                sideSign * SketchGeometryRelationLabelManager.MARKER_TANGENT_SCREEN_SPACING_PX * 0.5,
            );
            layouts.push(this.createMarkerLayout(midpoint, normalScreenOffset.add(tangentScreenOffset)));
        }
        return layouts;
    }

    
    /**
     * 创建关系标签布局对象。
     * @param anchorPosition 3D 锚点位置。
     * @param screenOffset 屏幕像素偏移。
     * @returns 标签布局对象。
     */
    private createMarkerLayout(anchorPosition: THREE.Vector3, screenOffset: THREE.Vector2): MarkerLayout {
        return {
            anchorPosition: anchorPosition.clone(),
            screenOffset: screenOffset.clone(),
        };
    }

    /**
     * 计算指定局部方向上的屏幕像素偏移。
     * 说明：
     * 1. 标签锚点保留在几何中点；
     * 2. 方向通过相机投影转换到屏幕空间；
     * 3. 无相机的测试环境回退到局部 X/Y 的屏幕映射。
     * @param edgeObject 标签所属边对象。
     * @param anchorPosition 标签锚点局部位置。
     * @param direction 局部方向。
     * @param distancePx 偏移距离，单位像素。
     * @returns 屏幕像素偏移。
     */
    private resolveScreenOffset(
        edgeObject: THREE.Object3D,
        anchorPosition: THREE.Vector3,
        direction: THREE.Vector3,
        distancePx: number,
    ): THREE.Vector2 {
        if (Math.abs(distancePx) <= 1e-8 || direction.lengthSq() <= 1e-8) {
            return new THREE.Vector2(0, 0);
        }

        const screenDirection = this.resolveProjectedScreenDirection(edgeObject, anchorPosition, direction);
        return screenDirection.multiplyScalar(distancePx);
    }

    /**
     * 将对象局部方向投影为屏幕方向。
     * @param edgeObject 标签所属边对象。
     * @param anchorPosition 标签锚点局部位置。
     * @param direction 局部方向。
     * @returns 归一化后的屏幕方向。
     */
    private resolveProjectedScreenDirection(
        edgeObject: THREE.Object3D,
        anchorPosition: THREE.Vector3,
        direction: THREE.Vector3,
    ): THREE.Vector2 {
        const camera = this.resolveViewerCamera();
        const viewportSize = this.resolveViewportSize();
        if (!camera || !viewportSize) {
            return this.resolveFallbackScreenDirection(direction);
        }

        const normalizedDirection = direction.clone();
        if (normalizedDirection.lengthSq() <= 1e-8) {
            return this.resolveFallbackScreenDirection(direction);
        }

        edgeObject.updateWorldMatrix(true, false);
        camera.updateMatrixWorld();
        const anchorWorld = edgeObject.localToWorld(anchorPosition.clone());
        const targetWorld = edgeObject.localToWorld(
            anchorPosition.clone().add(normalizedDirection.normalize()),
        );
        const anchorScreen = this.projectWorldToScreen(anchorWorld, camera, viewportSize.width, viewportSize.height);
        const targetScreen = this.projectWorldToScreen(targetWorld, camera, viewportSize.width, viewportSize.height);
        const screenDirection = targetScreen.sub(anchorScreen);

        if (screenDirection.lengthSq() <= 1e-8) {
            return this.resolveFallbackScreenDirection(direction);
        }

        return screenDirection.normalize();
    }

    /**
     * 解析 Viewer 当前相机。
     * @returns 当前相机；不可用时返回 null。
     */
    private resolveViewerCamera(): THREE.Camera | null {
        const viewerLike = this.viewer as unknown as {
            getCamera?: () => THREE.Camera | null;
            camera?: THREE.Camera | null;
        };

        return viewerLike.getCamera?.() ?? viewerLike.camera ?? null;
    }

    /**
     * 解析当前视口尺寸。
     * @returns 视口尺寸；不可用时返回 null。
     */
    private resolveViewportSize(): { width: number; height: number } | null {
        const container = (this.viewer as unknown as { container?: HTMLElement | null }).container ?? null;
        const width = container?.clientWidth ?? 0;
        const height = container?.clientHeight ?? 0;

        if (width <= 0 || height <= 0) {
            return null;
        }

        return { width, height };
    }

    /**
     * 将世界坐标投影到 CSS2D 屏幕坐标。
     * @param worldPosition 世界坐标。
     * @param camera 当前相机。
     * @param width 视口宽度。
     * @param height 视口高度。
     * @returns 屏幕坐标，原点位于左上角。
     */
    private projectWorldToScreen(
        worldPosition: THREE.Vector3,
        camera: THREE.Camera,
        width: number,
        height: number,
    ): THREE.Vector2 {
        const ndcPosition = worldPosition.clone().project(camera);
        return new THREE.Vector2(
            (ndcPosition.x * 0.5 + 0.5) * width,
            (-ndcPosition.y * 0.5 + 0.5) * height,
        );
    }

    /**
     * 在无相机环境下解析屏幕偏移方向。
     * @param direction 局部方向。
     * @returns 屏幕方向。
     */
    private resolveFallbackScreenDirection(direction: THREE.Vector3): THREE.Vector2 {
        const screenDirection = new THREE.Vector2(direction.x, -direction.y);
        if (screenDirection.lengthSq() <= 1e-8) {
            return new THREE.Vector2(1, 0);
        }

        return screenDirection.normalize();
    }
    

    /**
     * @description 按关系类型解析偏移方向（水平上下、竖直左右）。
     * @param markerType 标签类型。
     * @param tangent 线段切线方向。
     * @param markerBasePosition 标签基准点。
     * @param parentCenter 父级几何中心。
     * @returns 偏移方向向量。
     */
    private resolveStateOffsetDirection(
        markerType: SketchRelationKind | undefined,
        tangent: THREE.Vector3,
        markerBasePosition: THREE.Vector3,
        parentCenter: THREE.Vector3 | null,
    ): THREE.Vector3 {
        if (markerType === SketchRelationKind.Horizontal) {
            return this.resolveAxisSignedOffsetDirection('y', markerBasePosition, parentCenter);
        }
        if (markerType === SketchRelationKind.Vertical) {
            return this.resolveAxisSignedOffsetDirection('x', markerBasePosition, parentCenter);
        }
        if (markerType === SketchRelationKind.Tangent) {
            const useYAxis = Math.abs(tangent.x) >= Math.abs(tangent.y);
            return this.resolveAxisSignedOffsetDirection(
                useYAxis ? 'y' : 'x',
                markerBasePosition,
                parentCenter,
            );
        }
        return this.computeOffsetDirection(tangent);
    }

    /**
     * @description 沿指定坐标轴计算带符号偏移方向。
     * @param axis 偏移轴（x 或 y）。
     * @param markerBasePosition 标签基准点。
     * @param parentCenter 父级几何中心。
     * @returns 偏移方向向量。
     */
    private resolveAxisSignedOffsetDirection(
        axis: 'x' | 'y',
        markerBasePosition: THREE.Vector3,
        parentCenter: THREE.Vector3 | null,
    ): THREE.Vector3 {
        if (!parentCenter) {
            return axis === 'x' ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
        }
        if (axis === 'x') {
            return markerBasePosition.x >= parentCenter.x
                ? new THREE.Vector3(1, 0, 0)
                : new THREE.Vector3(-1, 0, 0);
        }
        return markerBasePosition.y >= parentCenter.y
            ? new THREE.Vector3(0, 1, 0)
            : new THREE.Vector3(0, -1, 0);
    }

    /**
     * 解析草图直线的局部中点与切线。
     * 说明：
     * 1. 直线端点不是线对象子节点，不能直接把点对象 position 当作线对象局部坐标；
     * 2. 先读取端点世界坐标，再转换到线对象局部坐标，可兼容草图组或线对象父级变换；
     * 3. 标签作为线对象子节点时，最终锚点必须使用线对象局部坐标。
     * @param edgeObject 草图直线对象。
     * @returns 中点与切线；不可用时返回 null。
     */
    private resolveSketchLineMidpointAndTangent(
        edgeObject: THREE.Object3D,
    ): { midpoint: THREE.Vector3; tangent: THREE.Vector3 } | null {
        if (!isSketchLineObject(edgeObject)) {
            return null;
        }
        // 确保对象树矩阵已更新，避免读取到旧的 world matrix。
        edgeObject.updateWorldMatrix(true, false);
        edgeObject.startPoint.updateWorldMatrix(true, false);
        edgeObject.endPoint.updateWorldMatrix(true, false);
        // 使用真实世界坐标作为中间值，避免混用点对象局部坐标和线对象局部坐标。
        const startWorld = edgeObject.startPoint.getWorldPosition(new THREE.Vector3());
        const endWorld = edgeObject.endPoint.getWorldPosition(new THREE.Vector3());
        const startLocal = edgeObject.worldToLocal(startWorld.clone());
        const endLocal = edgeObject.worldToLocal(endWorld.clone());
        const midpoint = startLocal.clone().add(endLocal).multiplyScalar(0.5);
        const tangent = endLocal.clone().sub(startLocal);
        if (tangent.lengthSq() < 1e-8) {
            // 退化线段使用稳定默认切线，保证标签仍可显示在锚点附近。
            return {
                midpoint,
                tangent: new THREE.Vector3(1, 0, 0),
            };
        }
        return {
            midpoint,
            tangent: tangent.normalize(),
        };
    }

    /**
     * @description 判断标签类型是否使用线段中点优先布局。
     * @param markerType 标签类型。
     * @returns 是否使用中点优先布局。
     */
    private shouldUseLineCenterPlacement(markerType: SketchRelationKind | undefined): boolean {
        if (!markerType) {
            return false;
        }
        return markerType === SketchRelationKind.Horizontal
            || markerType === SketchRelationKind.Vertical
            || markerType === SketchRelationKind.Tangent;
    }

    /**
     * @description 收集中点优先布局标签索引。
     * @param markerTypes 标签类型列表。
     * @returns 需要中点优先布局的索引列表。
     */
    private collectCenteredMarkerIndices(markerTypes: SketchRelationKind[]): number[] {
        const indices: number[] = [];
        markerTypes.forEach((markerType, index) => {
            if (this.shouldUseLineCenterPlacement(markerType)) {
                indices.push(index);
            }
        });
        return indices;
    }

    /**
     * @description 计算中点标签的对称分散值。
     * @param order 中点标签在中点组内顺序（从 0 开始）。
     * @param total 中点标签总数。
     * @returns 对称分散值（中心为 0）。
     */
    private computeCenteredMarkerSpread(order: number, total: number): number {
        if (total <= 1) {
            return 0;
        }
        return order - ((total - 1) / 2);
    }

    /**
     * @description 计算父级几何中心并转换到当前对象局部坐标系。
     * @param edgeObject 线对象。
     * @returns 父级中心的对象局部坐标；不可用时返回 null。
     */
    private resolveParentCenterInObjectSpace(edgeObject: THREE.Object3D): THREE.Vector3 | null {
        if (!edgeObject.parent) {
            return null;
        }
        const parentBounds = new THREE.Box3().setFromObject(edgeObject.parent);
        if (parentBounds.isEmpty()) {
            return null;
        }
        const centerInWorld = parentBounds.getCenter(new THREE.Vector3());
        return edgeObject.worldToLocal(centerInWorld);
    }

    /**
     * @description 根据父级中心决定法线偏移方向，优先向外侧偏移。
     * @param markerBasePosition 标签锚点基准位置（对象局部坐标）。
     * @param parentCenter 父级中心（对象局部坐标）。
     * @param defaultOffsetDirection 默认法线方向。
     * @returns 朝外侧的法线方向。
     */
    private resolveOutwardOffsetDirection(
        markerBasePosition: THREE.Vector3,
        parentCenter: THREE.Vector3,
        defaultOffsetDirection: THREE.Vector3,
    ): THREE.Vector3 {
        const fromCenterToMarker = markerBasePosition.clone().sub(parentCenter);
        if (fromCenterToMarker.lengthSq() < 1e-8) {
            return defaultOffsetDirection;
        }
        if (fromCenterToMarker.dot(defaultOffsetDirection) >= 0) {
            return defaultOffsetDirection;
        }
        return defaultOffsetDirection.clone().multiplyScalar(-1);
    }

    /**
     * 计算切线方向。
     * @param positionAttribute 顶点属性。
     * @param sampleIndex 采样索引。
     * @returns 切线向量。
     */
    private computeTangent(
        positionAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
        sampleIndex: number,
    ): THREE.Vector3 {
        const prevIndex = Math.max(0, sampleIndex - 1);
        const nextIndex = Math.min(positionAttribute.count - 1, sampleIndex + 1);
        const prevPosition = new THREE.Vector3(
            positionAttribute.getX(prevIndex),
            positionAttribute.getY(prevIndex),
            positionAttribute.getZ(prevIndex),
        );
        const nextPosition = new THREE.Vector3(
            positionAttribute.getX(nextIndex),
            positionAttribute.getY(nextIndex),
            positionAttribute.getZ(nextIndex),
        );
        const tangent = nextPosition.sub(prevPosition);
        if (tangent.lengthSq() < 1e-8) {
            return new THREE.Vector3(1, 0, 0);
        }
        return tangent.normalize();
    }

    /**
     * 计算偏移方向。
     * @param tangent 切线向量。
     * @returns 偏移向量。
     */
    private computeOffsetDirection(tangent: THREE.Vector3): THREE.Vector3 {
        const zAxis = new THREE.Vector3(0, 0, 1);
        const yAxis = new THREE.Vector3(0, 1, 0);
        const first = tangent.clone().cross(zAxis);
        if (first.lengthSq() > 1e-8) {
            return first.normalize();
        }
        const second = tangent.clone().cross(yAxis);
        if (second.lengthSq() > 1e-8) {
            return second.normalize();
        }
        return new THREE.Vector3(0, 1, 0);
    }

    /**
     * 更新标签对象布局。
     * @param markerObjects 标签对象列表。
     * @param markerTypes 标签类型列表。
     * @param markerKeys 标签键列表。
     * @param layouts 标签布局列表。
     * @param visible 可见性。
     */
    private updateMarkerObjectLayouts(
        markerObjects: SketchRelationMarkerLabelObject[],
        markerTypes: SketchRelationKind[],
        markerKeys: string[],
        layouts: MarkerLayout[],
        visible: boolean,
    ): void {
        markerObjects.forEach((markerObject, index) => {
            const markerType = markerTypes[index];
            const markerKey = markerKeys[index];
            const markerLayout = layouts[index];
            if (!markerType || !markerKey || !markerLayout) {
                markerObject.visible = false;
                markerObject.setScreenPixelOffset(0, 0, SketchGeometryRelationLabelManager.MARKER_ICON_SIZE_PX);
                return;
            }

            markerObject.position.copy(markerLayout.anchorPosition);
            markerObject.setScreenPixelOffset(
                markerLayout.screenOffset.x,
                markerLayout.screenOffset.y,
                SketchGeometryRelationLabelManager.MARKER_ICON_SIZE_PX,
            );
            markerObject.visible = visible;
            markerObject.setLayoutVisible(visible);
            markerObject.setMarkerIdentity(markerType, markerKey);
            this.applyMarkerObjectVisualState(markerObject, markerType, markerKey);
        });
    }

    /**
     * 创建单个标签对象。
     * @param markerType 标签类型。
     * @param markerKey 标签键。
     * @returns 标签对象；失败返回 null。
     */
    private createMarkerObject(markerType: SketchRelationKind, markerKey: string): SketchRelationMarkerLabelObject | null {
        const markerElement = document.createElement('img');
        markerElement.draggable = false;
        markerElement.alt = markerType;
        markerElement.width = SketchGeometryRelationLabelManager.MARKER_ICON_SIZE_PX;
        markerElement.height = SketchGeometryRelationLabelManager.MARKER_ICON_SIZE_PX;
        markerElement.style.width = `${SketchGeometryRelationLabelManager.MARKER_ICON_SIZE_PX}px`;
        markerElement.style.height = `${SketchGeometryRelationLabelManager.MARKER_ICON_SIZE_PX}px`;
        markerElement.style.pointerEvents = 'none';
        markerElement.style.userSelect = 'none';

        const markerObject = new SketchRelationMarkerLabelObject(markerElement, markerType, markerKey);
        this.applyMarkerObjectVisualState(markerObject, markerType, markerKey);
        return markerObject;
    }

    /**
     * 应用标签对象视觉状态。
     * @param markerObject 标签对象。
     * @param markerType 标签关系类型。
     * @param markerKey 标签键。
     */
    private applyMarkerObjectVisualState(
        markerObject: SketchRelationMarkerLabelObject,
        markerType: SketchRelationKind,
        markerKey: string,
    ): void {
        if (this.markerForceHidden) {
            markerObject.visible = false;
            return;
        }
        const state = this.resolveMarkerVisualState(markerKey);
        if (state === RelationMarkerVisualState.Default) {
            markerObject.visible = false;
            return;
        }
        markerObject.visible = markerObject.layoutVisible;
        void this.applyMarkerSvgByState(markerObject, markerType, state);
    }

    /**
     * 按状态更新 SVG 图标。
     * @param markerObject 标签对象。
     * @param markerType 标签关系类型。
     * @param state 视觉状态。
     */
    private async applyMarkerSvgByState(
        markerObject: SketchRelationMarkerLabelObject,
        markerType: SketchRelationKind,
        state: RelationMarkerVisualState,
    ): Promise<void> {
        const markerElement = markerObject.element as HTMLImageElement | undefined;
        if (!markerElement) {
            return;
        }

        const nextVersion = markerObject.bumpRenderVersion();
        const currentVersion = nextVersion;

        const svgTemplate = await this.loadSvgTemplate(markerType);
        const style = MARKER_VISUAL_STYLE_MAP[state];
        const svgContent = svgTemplate
            .replaceAll(SVG_BG_COLOR_TOKEN, style.backgroundColor)
            .replaceAll(SVG_GLYPH_COLOR_TOKEN, style.glyphColor);
        const encodedSvg = encodeURIComponent(svgContent);
        const dataUrl = `data:image/svg+xml;utf8,${encodedSvg}`;

        if (!markerObject.isCurrentRenderVersion(currentVersion)) {
            return;
        }

        markerElement.src = dataUrl;
    }

    /**
     * 加载并缓存 SVG 模板。
     * @param relationKind 关系类型。
     * @returns SVG 模板内容。
     */
    private loadSvgTemplate(relationKind: SketchRelationKind): Promise<string> {
        const cachedPromise = this.svgTemplatePromiseByKind.get(relationKind);
        if (cachedPromise) {
            return cachedPromise;
        }

        const svgUrl = SVG_URL_MAP[relationKind];
        if (!svgUrl) {
            return Promise.reject(new Error(`草图几何关系 ${relationKind} 未配置 SVG 图标。`));
        }
        const loadPromise = fetch(svgUrl).then(async (response) => {
            if (!response.ok) {
                throw new Error(`加载 SVG 失败: ${svgUrl}`);
            }
            return response.text();
        });
        this.svgTemplatePromiseByKind.set(relationKind, loadPromise);
        return loadPromise;
    }

    /**
     * 通过新键集合替换目标状态集合。
     * @param target 目标状态集合。
     * @param nextKeys 新键列表。
     */
    private replaceStateKeySet(target: Set<string>, nextKeys: string[]): void {
        target.clear();
        nextKeys.forEach((key) => {
            if (!key) {
                return;
            }
            target.add(key);
        });
    }

    /**
     * 刷新全部标签视觉状态。
     */
    private refreshAllMarkerVisualStates(): void {
        this.markerRecordsByObjectId.forEach((record) => {
            record.markerObjects.forEach((markerObject, index) => {
                const markerType = record.markerTypes[index];
                const markerKey = record.markerKeys[index];
                if (!markerType || !markerKey) {
                    return;
                }
                this.applyMarkerObjectVisualState(markerObject, markerType, markerKey);
            });
        });
    }

    /**
     * 解析标签键对应的视觉状态。
     * 优先级：移入 > 点击选中 > 菜单选中 > 默认。
     * @param markerKey 标签键。
     * @returns 视觉状态。
     */
    private resolveMarkerVisualState(markerKey: string): RelationMarkerVisualState {
        if (this.hoveredMarkerKeySet.has(markerKey)) {
            return RelationMarkerVisualState.Hover;
        }
        if (this.clickSelectedMarkerKeySet.has(markerKey)) {
            return RelationMarkerVisualState.ClickSelected;
        }
        if (this.menuSelectedMarkerKeySet.has(markerKey)) {
            return RelationMarkerVisualState.MenuSelected;
        }
        return RelationMarkerVisualState.Default;
    }


    /**
     * 比较标签类型列表是否变化。
     * @param current 当前类型列表。
     * @param expected 目标类型列表。
     * @returns 是否变化。
     */
    private hasMarkerTypeChanged(current: SketchRelationKind[], expected: SketchRelationKind[]): boolean {
        if (current.length !== expected.length) {
            return true;
        }
        for (let index = 0; index < current.length; index += 1) {
            if (current[index] !== expected[index]) {
                return true;
            }
        }
        return false;
    }

    /**
     * 比较标签键列表是否变化。
     * @param current 当前键列表。
     * @param expected 目标键列表。
     * @returns 是否变化。
     */
    private hasMarkerKeyChanged(current: string[], expected: string[]): boolean {
        if (current.length !== expected.length) {
            return true;
        }
        for (let index = 0; index < current.length; index += 1) {
            if (current[index] !== expected[index]) {
                return true;
            }
        }
        return false;
    }

    /**
     * 收集对象树下已跟踪标签的对象 ID 集合。
     * @param object 目标对象。
     * @returns 已跟踪的对象 ID 集合。
     */
    private collectTrackedObjectIds(object: THREE.Object3D): Set<string> {
        const trackedIds = new Set<string>();
        object.traverse((child) => {
            if (this.markerRecordsByObjectId.has(child.uuid)) {
                trackedIds.add(child.uuid);
            }
        });
        return trackedIds;
    }

    /**
     * 从指定对象的标签记录中移除目标约束的标签。
     * @param objectId 对象 ID。
     * @param constraintId 约束 ID。
     */
    private removeConstraintMarkerFromRecord(objectId: string, constraintId: number): void {
        const record = this.markerRecordsByObjectId.get(objectId);
        if (!record) {
            return;
        }

        const targetKey = this.buildMarkerKey(objectId, constraintId);
        const targetIndex = record.markerKeys.indexOf(targetKey);
        if (targetIndex < 0) {
            return;
        }

        // 分离目标标签对象。
        this.detachMarkerObject(record.markerObjects[targetIndex]);

        // 从记录的各数组中移除对应项。
        record.markerObjects.splice(targetIndex, 1);
        record.markerTypes.splice(targetIndex, 1);
        record.markerKeys.splice(targetIndex, 1);

        // 若记录为空则删除整条记录。
        if (record.markerObjects.length <= 0) {
            this.markerRecordsByObjectId.delete(objectId);
        }
    }

    /**
     * 从父节点分离标签对象。
     * @param markerObject 标签对象。
     */
    private detachMarkerObject(markerObject: SketchRelationMarkerLabelObject): void {
        if (markerObject.parent) {
            markerObject.parent.remove(markerObject);
        }
    }

    /**
     * 构建标签唯一键。
     * @param objectId 对象 ID。
     * @param constraintId 约束 ID。
     * @returns 标签键。
     */
    private buildMarkerKey(objectId: string, constraintId: number): string {
        return `${objectId}:${constraintId}`;
    }

}
